/**
 * Client-side (browser) remote storage configured to use SealedDeck.Tech API.
 * 
 * RemoteStorage functions:
 *  - savePoolState(poolState, ...sourceIds)
 *  - saveTierList(poolState, set, ...sourceIds)
 *  - readPoolState(poolStateId)
 *  - readTierList(tierListId)
 */
class BrowserRemoteStorage {

  constructor() {
    // configure access to the SealedDeck.Tech API
    const SEALEDDECKTECH_API_URI = `${window.location.origin}/api`;

    const request = async (path, options = {}) => {
      const response = await fetch(`${SEALEDDECKTECH_API_URI}${path}`, options);
      
      // Safely parse JSON payload or fall back to empty object
      const responseJson = await response.json().catch(() => ({}));

      // Reject the promise if the server returns an error status code
      if (!response.ok) {
        const error = new Error(responseJson.message || `HTTP Error ${response.status}`);
        error.statusCode = response.status;
        error.responseData = responseJson;
        throw error;
      }

      return { status: response.status, json: responseJson };
    };

    // Async function for CardPool GET requests
    this.apiPoolsGET = (poolStateId) => 
      // Always request `columns=true` to get the column-formatted cardpool data
      request(`/pools/${poolStateId}?columns=true`);

    // Async function for TierList GET requests
    this.apiTierListsGET = (tierListId) => 
      // Always request `columns=true` to get the column-formatted cardpool data
      request(`/tierlists/${tierListId}?columns=true`);

    // Async function for PoolState POST requests
    this.apiPOST = (path, poolState) => request(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(poolState)
    });
  }

  async savePoolState(poolState, ...sourceIds) {
    // flatten any provided SourceID objects into a single JSON object
    const sourceIdsJson = sourceIds.reduce((json, sourceId) => {
      return { ...json, ...sourceId?.toJsonObject() };
    }, {});

    const mergedPoolState = { ...poolState, ...sourceIdsJson}

    // POST the merged cardpool state to the API
    return this.apiPOST('/pools', mergedPoolState)
      // return the `poolId` from a successful response
      .then(({ json }) => json.poolId)
      .catch((err) => {
        console.error(`Error saving PoolState;`, err.statusCode, err);
        throw err;
      });
  }

  async saveTierList(poolState, set, ...sourceIds) {
    // flatten any provided SourceID objects into a single JSON object
    const sourceIdsJson = sourceIds.reduce((json, sourceId) => {
      return { ...json, ...sourceId?.toJsonObject() };
    }, {});

    const mergedPoolState = { ...poolState, set, ...sourceIdsJson}

    // POST the merged cardpool state to the API
    return this.apiPOST('/tierlists', mergedPoolState)
      // return the TierList ID from a successful response
      .then(({ json }) => json.tierlist_id)
      .catch((err) => {
        console.error(`Error saving TierList;`, err.statusCode, err);
        throw err;
      });
  }

  async readPoolState(poolStateId) {
    return this.apiPoolsGET(poolStateId)
      .then(({ json }) => json)
      .catch((err) => {
        console.warn(`Error reading PoolState '${poolStateId}' -`, err.message);
        throw err;
      });
  }

  async readTierList(tierListId) {
    return this.apiTierListsGET(tierListId)
      .then(({ json }) => json)
      .catch((err) => {
        console.warn(`Error reading TierList '${tierListId}' -`, err.message);
        throw err;
      });
  }
}

/* Explicit CommonJS module exports */
module.exports = new BrowserRemoteStorage();
